import { useEffect, useRef, useState, type ReactNode } from "react";
import type { BusyConversationDTO, MeetingDTO, PlatformEvent, RunDTO } from "../../../shared/types";
import { AgentAvatar } from "../../components/AgentAvatar";
import { spendLine, UsualMark } from "../../components/ConversationSpend";
import { CostBreakdown, RunCost } from "../../components/CostBreakdown";
import { List, ListRow, listRowCls } from "../../components/List";
import { Button, compact, duration, PageHeader, Sparkline, StatusBadge, timeAgo } from "../../components/ui";
import { get, post } from "../../lib/api";
import { Link } from "../../lib/router";
import { realtime } from "../../lib/ws";
import { ACTIVE, willJoin, type CalendarView } from "./Meetings";

type Hour = { hour: string; runs: number; failed: number; cost: number; billed: number; estimated: number; proxy_calls: number };
type Stats = {
  running: number; queued: number; runs_24h: number; failed_24h: number; cost_24h: number; proxy_calls_24h: number;
  billed_24h: number; estimated_24h: number; api_calls_24h: number;
  cache_rebuilds_24h: number; cache_reuse_pct_24h: number;
  conversations: number; agents: number; hourly?: Hour[];
  /** The five conversations that spent the most in the last 24 hours, each against its usual day. */
  busiest?: BusyConversationDTO[];
};

/** "Wed 11:00", or "11:00" when it is today. */
const whenShort = (iso: string) => {
  const date = new Date(iso);
  const today = date.toDateString() === new Date().toDateString();
  return date.toLocaleString(undefined, { ...(today ? {} : { weekday: "short" }), hour: "2-digit", minute: "2-digit" });
};

const EVENT_TONE: Record<string, string> = {
  "run.started": "bg-info", "run.finished": "bg-success", "run.failed": "bg-danger", "tool.call": "bg-violet-400", "proxy.call": "bg-amber-400",
};

function StatCard({ label, value, trend, tone = "text-haze-deep", pulse = false, alert = false, breakdown }: {
  label: string; value: string | number; trend?: number[]; tone?: string; pulse?: boolean; alert?: boolean; breakdown?: ReactNode;
}) {
  return (
    <div className="card animate-fade-up flex flex-col justify-between overflow-hidden p-4">
      <div className="flex min-w-0 items-center gap-1.5 text-xs font-medium text-muted">
        {pulse && (
          <span className="relative flex size-2">
            <span className="absolute inline-flex size-full animate-ping rounded-full bg-info opacity-60" />
            <span className="relative inline-flex size-2 rounded-full bg-info" />
          </span>
        )}
        <span className="min-w-0 truncate">{label}</span>
        {breakdown}
      </div>
      <div className={`mt-2 truncate text-title leading-none font-semibold tracking-tight tabular-nums sm:text-title ${alert ? "text-danger" : ""}`}>{value}</div>
      <div className={`-mx-4 -mb-4 mt-3 ${tone}`}>{trend ? <Sparkline values={trend} /> : <div className="h-8" />}</div>
    </div>
  );
}

function RunRow({ run, showCancel }: { run: RunDTO; showCancel: boolean }) {
  const task = run.task.split("\n")[0]!.trim();
  return (
    <div className={`flex items-center gap-3 ${listRowCls} transition-colors hover:bg-fill-subtle`}>
      <AgentAvatar agent={run.agentSlug} name={run.agentName} />
      <Link to={`/admin/runs/${run.id}`} className="min-w-0 flex-1">
        <div className="flex min-w-0 flex-wrap items-center gap-x-2">
          <span className="truncate text-sm font-medium">{run.agentName}</span>
          <StatusBadge status={run.status} />
          {run.depth > 0 && <span className="text-tiny text-faint">↳ delegated</span>}
          {run.trigger !== "chat" && run.trigger !== "unknown" && <span className="text-tiny text-faint">{run.trigger}</span>}
          {run.principal === "agent" && <span className="text-tiny text-faint">own behalf</span>}
        </div>
        <div className="mt-0.5 truncate text-sm text-muted">{task}</div>
        <div className="mt-0.5 text-tiny text-faint tabular-nums sm:hidden">
          {run.startedAt ? duration(run.startedAt, run.finishedAt) : timeAgo(run.createdAt)} · <RunCost usd={run.costUsd} paidBy={run.paidBy} />
        </div>
      </Link>
      <div className="hidden shrink-0 items-center gap-5 text-right text-xs tabular-nums text-muted sm:flex">
        <div className="w-16">
          <div className="text-fg">{run.startedAt ? duration(run.startedAt, run.finishedAt) : "—"}</div>
          <div className="text-tiny text-faint">{timeAgo(run.createdAt)}</div>
        </div>
        <div className="w-20">
          <div className="text-fg">{run.inputTokens ? compact(run.inputTokens) : "—"}</div>
          <div className="text-tiny text-faint">{run.outputTokens ? `${compact(run.outputTokens)} out` : "tokens"}</div>
        </div>
        <div className="w-16 text-fg"><RunCost usd={run.costUsd} paidBy={run.paidBy} /></div>
      </div>
      {showCancel && (
        <Button variant="danger" size="sm" className="shrink-0" onClick={() => post(`/api/runs/${run.id}/cancel`)}>
          Cancel
        </Button>
      )}
    </div>
  );
}

export function AdminLive() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [runs, setRuns] = useState<RunDTO[]>([]);
  const [feed, setFeed] = useState<PlatformEvent[]>([]);
  const [, tick] = useState(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [calendar, setCalendar] = useState<CalendarView | null>(null);
  const [meetings, setMeetings] = useState<MeetingDTO[]>([]);

  const load = () => {
    get<Stats>("/api/stats").then(setStats, () => {});
    get<RunDTO[]>("/api/runs?limit=100").then(setRuns, () => {});
    get<CalendarView>("/api/meetings/calendar").then(setCalendar, () => {});
    get<MeetingDTO[] | { meetings: MeetingDTO[] }>("/api/meetings").then((r) => setMeetings(Array.isArray(r) ? r : (r?.meetings ?? [])), () => {});
  };

  useEffect(() => {
    load();
    const interval = setInterval(() => tick((n) => n + 1), 1000);
    const off = realtime.subscribe("admin", (e) => {
      if (e.type !== "assistant.delta") setFeed((f) => [e, ...f].slice(0, 150));
      if (e.type.startsWith("run.")) {
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(load, 300);
      }
    });
    return () => {
      off();
      clearInterval(interval);
    };
  }, []);

  // Meetings Pandas is in right now, and the ones it is set to join next.
  const inCall = meetings.filter((m) => ACTIVE.includes(m.status));
  const upcoming = (calendar?.events ?? [])
    .filter((e) => !e.meetingId && e.platform !== "unknown" && Date.parse(e.endsAt) > Date.now() && willJoin(e, calendar!.settings.autoJoin))
    .slice(0, 5);

  const active = runs.filter((r) => r.status === "running" || r.status === "queued");
  const recent = runs.filter((r) => r.status !== "running" && r.status !== "queued").slice(0, 30);
  const series = (key: keyof Omit<Hour, "hour">) => stats?.hourly?.map((h) => Number(h[key]));

  return (
    <div className="pb-10">
      <PageHeader
        title="Live"
        subtitle="Everything running on the platform, updated in real time."
        actions={
          <span className="inline-flex items-center gap-2 rounded-full bg-surface px-3 py-1 text-xs font-medium text-muted shadow-sm ring-1 ring-line">
            <span className="size-1.5 rounded-full bg-success" /> Realtime
          </span>
        }
      />

      <div className="grid grid-cols-2 gap-3 px-4 sm:grid-cols-3 md:px-6 xl:grid-cols-6 [&>*]:min-w-0">
        {stats ? (
          <>
            <StatCard label="Running" value={stats.running} pulse={stats.running > 0} tone="text-sky-400" />
            <StatCard label="Queued" value={stats.queued} tone="text-faint" />
            <StatCard label="Runs · 24h" value={stats.runs_24h} trend={series("runs")} />
            <StatCard label="Failed · 24h" value={stats.failed_24h} trend={series("failed")} tone="text-red-300" alert={stats.failed_24h > 0} />
            <StatCard
              label="Cost · 24h"
              value={`$${stats.cost_24h.toFixed(2)}`}
              trend={series("cost")}
              tone="text-emerald-300"
              breakdown={<CostBreakdown billedUsd={stats.billed_24h} estimatedUsd={stats.estimated_24h} toolCalls={stats.api_calls_24h} />}
            />
            <StatCard label="Read from cache · 24h" value={`${stats.cache_reuse_pct_24h}%`} tone="text-emerald-300" />
            <StatCard label="Cache rebuilds · 24h" value={stats.cache_rebuilds_24h} tone="text-amber-300" alert={stats.cache_rebuilds_24h > 20} />
            <StatCard label="Connector calls · 24h" value={compact(stats.proxy_calls_24h)} trend={series("proxy_calls")} tone="text-violet-300" />
          </>
        ) : (
          Array.from({ length: 6 }, (_, i) => <div key={i} className="card h-[118px] animate-pulse" />)
        )}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 px-4 md:px-6 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="min-w-0 space-y-6">
          <List
            title={
              <>
                Active runs
                {active.length > 0 && <span className="ml-2 rounded-full bg-info/10 px-2 py-0.5 align-middle text-xs font-normal tracking-normal text-info">{active.length}</span>}
              </>
            }
            empty={
              <div className="flex flex-col items-center gap-1">
                <img src="/favicon.png" alt="Pandito" className="mb-1 size-12 rounded-2xl shadow-sm ring-1 ring-line" />
                <span className="text-sm font-medium text-fg">All quiet</span>
                <span className="text-xs text-muted">Nothing is running right now.</span>
              </div>
            }
          >
            {active.map((r) => <RunRow key={r.id} run={r} showCancel />)}
          </List>
          <List title="Busiest conversations · 24h" hint="Watched, never limited" empty="No runs in the last 24 hours.">
            {(stats?.busiest ?? []).map((c) => (
              <ListRow
                key={c.conversationId}
                to={`/admin/conversations/${c.conversationId}`}
                title={<span className="truncate font-medium">{c.title || "(untitled)"}</span>}
                subtitle={<span className="tabular-nums">{c.agentName}{c.userName ? ` · ${c.userName}` : ""} · {spendLine(c)}</span>}
                trailing={<><CostBreakdown billedUsd={c.billedUsd} estimatedUsd={c.estimatedUsd} /><UsualMark observed={c} /></>}
              />
            ))}
          </List>
          <List
            title={
              <>
                Meetings
                {inCall.length > 0 && <span className="ml-2 rounded-full bg-success/10 px-2 py-0.5 align-middle text-xs font-normal tracking-normal text-success">{inCall.length} in call</span>}
              </>
            }
            empty="Pandas is not scheduled for any meeting."
          >
            {inCall.map((m) => (
              <ListRow
                key={m.id}
                to="/admin/meetings"
                leading={<span className="block size-2 animate-pulse rounded-full bg-success" />}
                title={<span className="truncate font-medium">{m.title || "(untitled)"}</span>}
                subtitle={`Pandas is ${m.status.replace("_", " ")} · for ${m.requestedByName}`}
                trailing={<span className="text-tiny text-faint">{timeAgo(m.createdAt)}</span>}
              />
            ))}
            {upcoming.map((e) => (
              <ListRow
                key={e.id}
                to="/admin/meetings"
                leading={<span className="block w-20 text-xs text-muted tabular-nums">{whenShort(e.startsAt)}</span>}
                title={<span className="truncate">{e.title}</span>}
                subtitle={`${e.attendeeCount} guests · ${e.organizerEmail ?? "no organizer"}`}
                trailing={<span className="text-tiny font-medium text-success">will join</span>}
              />
            ))}
          </List>
          <List title="Recent runs" empty="No runs yet.">
            {recent.map((r) => <RunRow key={r.id} run={r} showCancel={false} />)}
          </List>
        </div>

        <List
          scroll="tall"
          title="Event feed"
          className="xl:sticky xl:top-4 xl:self-start"
          empty={
            <div className="flex items-center justify-center gap-2 text-xs text-faint">
              <span className="relative flex size-2">
                <span className="absolute inline-flex size-full animate-ping rounded-full bg-haze-deep opacity-60" />
                <span className="relative inline-flex size-2 rounded-full bg-haze-deep" />
              </span>
              Listening for events…
            </div>
          }
        >
          {feed.map((e, i) => (
            <Link
              key={`${e.runId}-${e.seq}-${i}`}
              to={`/admin/runs/${e.runId}`}
              className={`animate-fade-up flex items-center gap-2.5 ${listRowCls} text-xs transition-colors hover:bg-fill-subtle`}
            >
              <span className={`size-1.5 shrink-0 rounded-full ${EVENT_TONE[e.type] ?? "bg-faint"}`} />
              <span className="w-16 shrink-0 font-mono text-tiny text-faint tabular-nums">
                {new Date(e.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
              </span>
              <span className="shrink-0 font-medium text-muted">{e.type}</span>
              <span className="truncate text-muted">
                {e.type === "tool.call" ? e.payload.name : e.type === "proxy.call" ? `${e.payload.connector} ${e.payload.status}` : e.payload?.agent ?? ""}
              </span>
            </Link>
          ))}
        </List>
      </div>
    </div>
  );
}
